import * as React from 'react';
import { observer } from 'mobx-react-lite';
import ReactPhoneInput from 'react-phone-input-2';
import { PrimaryButton } from '@fluentui/react/lib/Button';
import { sendLocalizedTextMessage } from '../actions/sendLocalizedTextMessage';
import * as phoneAppCardUtils from '../utils/phoneAppCardUtils';
import { getIsDarkTheme } from 'owa-fabric-theme';
import { logUsage } from 'owa-analytics';
import { OutlookMobileContainer } from '../utils/OutlookMobileContainer';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getGuid } from 'owa-guid';
import {
    headerBusiness,
    headerConsumer,
    titleBusiness,
    titleConsumer,
    smsTextPlaceHolder,
    mobileComponentDisclaimerText,
    sendButtonTextBusiness,
    sendButtonTextConsumer,
    messageSentConfirmation,
    messageNotSentRetryMessage,
} from './GetOutlookMobileComponent.locstring.json';
import loc, { format } from 'owa-localize';
import { displayQRCodeForConsumer } from '../utils/getQRCodeForConsumer';
import { displayQRCodeForEnterprise } from '../utils/getQRCodeForEnterprise';
import { setPhoneAppCardErrorState } from '../mutators/setPhoneAppCardErrorState';
import { phoneAppCardStore } from '../store/store';
import { PhoneAppCardErrorState } from '../store/schema/PhoneAppCardState';
import isBusiness from 'owa-session-store/lib/utils/isBusiness';
import { getMobileContainerName } from '../utils/getMobileContainerName';

const styles = require('./PhoneAppCard.scss');

export const GetOutlookMobileComponent = observer(function GetOutlookMobileComponent(props: {
    containerName: OutlookMobileContainer;
    sendButtonCallback?: () => void;
    onComponentUnmount?: () => void;
}) {
    const [phoneNumber, setphoneNumber] = React.useState<string>('empty');
    const [countryCode, setCountryCode] = React.useState<string>('');
    const [disableSend, setDisableSend] = React.useState<boolean>(true);

    const [timerId, setTimerId] = React.useState<number>(0);

    const onSendButtonClick = React.useCallback(() => {
        setDisableSend(true);
        let timeoutId = window.setTimeout(() => {
            setDisableSend(false);
        }, 30000);
        setTimerId(timeoutId);
        sendLocalizedTextMessage(phoneNumber, countryCode, getGuid(), props.containerName);

        if (isFeatureEnabled('rp-emptyStatePhoneCardQrExp')) {
            if (isBusiness()) {
                displayQRCodeForEnterprise();
            } else {
                displayQRCodeForConsumer();
            }
        }

        if (props.sendButtonCallback) {
            props.sendButtonCallback();
        }
    }, [phoneNumber, countryCode]);

    const onPhoneInputChange = React.useCallback(
        (phoneNo: string, country: any) => {
            setPhoneAppCardErrorState(PhoneAppCardErrorState.Init);
            if (phoneNo.length > 4) {
                setDisableSend(false);
            } else {
                setDisableSend(true);
            }

            setphoneNumber(phoneNo);
            setCountryCode(country.countryCode);
        },
        [phoneNumber, countryCode]
    );

    let reactInputCssElement: HTMLLinkElement;

    React.useEffect(() => {
        return () => {
            if (timerId != 0) {
                window.clearTimeout(timerId);
            }
        };
    }, [timerId]);

    React.useEffect(() => {
        reactInputCssElement = phoneAppCardUtils.mountReactPhoneInputCss();

        logUsage(
            'GetMobileComponentLoad',
            { containerName: getMobileContainerName(props.containerName) },
            { isCore: true }
        );
        return () => {
            if (props.onComponentUnmount) {
                props.onComponentUnmount();
            }
            if (reactInputCssElement) {
                document.body.removeChild(reactInputCssElement);
            }
        };
    }, []);

    return (
        /* tslint:disable:react-no-dangerous-html */
        //its safe to use innerHTML as content is static and is coming from locstrings
        <>
            <div className={styles.phoneAppHeader}>
                <div className={styles.phoneCardTitle}>
                    {loc(isBusiness() ? headerBusiness : headerConsumer)}
                </div>
                <div className={styles.phoneCardDesc}>
                    {loc(isBusiness() ? titleBusiness : titleConsumer)}
                </div>
            </div>
            <div className={styles.phoneAppCard}>
                <div className={styles.phoneSmsDiv}>
                    <div className={styles.phoneSmsInput}>
                        <div
                            className={
                                getIsDarkTheme()
                                    ? styles.phoneInputWrapperDark
                                    : styles.phoneInputWrapper
                            }>
                            <ReactPhoneInput
                                defaultCountry="us"
                                value={phoneNumber}
                                placeHolder={loc(smsTextPlaceHolder)}
                                onlyCountries={phoneAppCardUtils.allowedCountriesPhoneInput}
                                disableAreaCodes={true}
                                localization={phoneAppCardUtils.localizedCountriesPhoneInput}
                                onChange={onPhoneInputChange}
                            />
                            <PrimaryButton onClick={onSendButtonClick} disabled={disableSend}>
                                {loc(
                                    isBusiness() ? sendButtonTextBusiness : sendButtonTextConsumer
                                )}
                            </PrimaryButton>
                        </div>
                    </div>
                    <div className={styles.smsSendConfirmation}>
                        {disableSend && (
                            <div>
                                {phoneAppCardStore().errorState === PhoneAppCardErrorState.Success
                                    ? loc(messageSentConfirmation)
                                    : phoneAppCardStore().errorState ===
                                      PhoneAppCardErrorState.Failure
                                    ? loc(messageNotSentRetryMessage)
                                    : ''}
                            </div>
                        )}
                    </div>
                    <div className={styles.phoneSmsDesc}>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: format(
                                    loc(mobileComponentDisclaimerText),
                                    'https://go.microsoft.com/fwlink/?linkid=2044390',
                                    'https://go.microsoft.com/fwlink/?linkid=872595'
                                ),
                            }}
                        />
                    </div>
                    <div className={styles.phoneContainer}>
                        <img
                            className={styles.outlookMobileApps}
                            src={
                                getIsDarkTheme()
                                    ? phoneAppCardUtils.outlookMobileAppsDarkTheme
                                    : phoneAppCardUtils.outlookMobileApps
                            }
                        />
                        <div className={styles.phoneGradient} />
                    </div>
                </div>
            </div>
        </>
        /* tslint:enable:react-no-dangerous-html */
    );
});
